if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.initViewportVideoAutoplay();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      disconnectedCallback() {
        if (this.viewportVideoObserver) this.viewportVideoObserver.disconnect();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        const mobileVideo = activeItem.querySelector('.product__mobile-inline-video');
        if (mobileVideo && window.innerWidth < 750) {
          this.elements.viewer.querySelectorAll('.product__mobile-inline-video').forEach((video) => {
            if (video !== mobileVideo) video.pause();
          });
          this.playViewportVideo(activeItem);
          return;
        }

        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      initViewportVideoAutoplay() {
        if (!this.elements.viewer || !('IntersectionObserver' in window)) return;

        this.viewportVideoObserver = new IntersectionObserver(this.onViewportVideoIntersection.bind(this), {
          threshold: [0, 0.25, 0.5, 1],
        });

        this.elements.viewer.querySelectorAll('.product__media-item').forEach((slide) => {
          if (!this.isVideoSlide(slide)) return;
          this.viewportVideoObserver.observe(slide);
        });

        if (window.innerWidth < 750) {
          this.elements.viewer.querySelectorAll('.product__mobile-inline-video').forEach((video, index) => {
            if (video.dataset.inlineBound !== 'true') {
              video.dataset.inlineBound = 'true';

              const markReady = () => video.classList.add('is-ready');
              video.addEventListener('loadeddata', markReady);
              video.addEventListener('canplay', markReady);
              video.addEventListener('playing', markReady);
            }

            if (video.dataset.loopBound !== 'true') {
              video.dataset.loopBound = 'true';
              video.addEventListener('ended', () => {
                video.currentTime = 0;
                const playPromise = video.play();
                if (playPromise?.catch) playPromise.catch(() => {});
              });
            }
            video.preload = index < 3 ? 'auto' : 'metadata';
            if (index < 3 && video.dataset.preloadInitialized !== 'true') {
              video.dataset.preloadInitialized = 'true';
              video.load();
            }
          });

          const activeSlide =
            this.elements.viewer.querySelector('.product__media-item.is-active') ||
            this.elements.viewer.querySelector('.product__media-item');
          if (activeSlide) this.playViewportVideo(activeSlide);
        }
      }

      isVideoSlide(slide) {
        if (slide.querySelector('.product__mobile-inline-video')) return true;

        const deferredMedia = slide.querySelector('deferred-media');
        if (!deferredMedia) return false;
        const templateMedia = deferredMedia.querySelector('template')?.content?.firstElementChild;
        if (!templateMedia) return false;
        return ['VIDEO', 'IFRAME'].includes(templateMedia.nodeName);
      }

      onViewportVideoIntersection(entries) {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.25) {
            this.playViewportVideo(entry.target);
            return;
          }

          this.pauseViewportVideo(entry.target);
        });
      }

      playViewportVideo(slide) {
        const mobileVideo = slide.querySelector('.product__mobile-inline-video');
        if (mobileVideo && window.innerWidth < 750) {
          this.elements.viewer.querySelectorAll('.product__mobile-inline-video').forEach((video) => {
            if (video !== mobileVideo) video.pause();
          });
          mobileVideo.muted = true;
          mobileVideo.defaultMuted = true;
          mobileVideo.playsInline = true;
          mobileVideo.setAttribute('muted', '');
          mobileVideo.setAttribute('playsinline', '');
          mobileVideo.preload = 'auto';
          if (mobileVideo.dataset.preloadInitialized !== 'true') {
            mobileVideo.dataset.preloadInitialized = 'true';
            mobileVideo.load();
          }

          const attemptPlay = () => {
            const playPromise = mobileVideo.play();
            if (playPromise?.catch) playPromise.catch(() => {});
          };

          if (mobileVideo.readyState < 2) {
            mobileVideo.addEventListener('loadeddata', attemptPlay, { once: true });
          }

          const playPromise = mobileVideo.play();
          if (playPromise?.catch) playPromise.catch(() => {});
          return;
        }

        const deferredMedia = slide.querySelector('deferred-media');
        if (!deferredMedia) return;

        if (!deferredMedia.hasAttribute('loaded')) {
          if (typeof deferredMedia.loadContent === 'function') {
            deferredMedia.loadContent(false);
          } else {
            deferredMedia.querySelector('.deferred-media__poster')?.click();
          }
        }

        const video = deferredMedia.querySelector('video');
        if (video) {
          video.muted = true;
          video.defaultMuted = true;
          video.setAttribute('muted', '');
          video.playsInline = true;
          video.setAttribute('playsinline', '');
          video.setAttribute('autoplay', '');
          const playPromise = video.play();
          if (playPromise?.catch) playPromise.catch(() => {});
          return;
        }

        const iframe = deferredMedia.querySelector('iframe');
        if (!iframe) return;

        if (iframe.classList.contains('js-youtube')) {
          iframe.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
          return;
        }

        if (iframe.classList.contains('js-vimeo')) {
          iframe.contentWindow?.postMessage('{"method":"play"}', '*');
        }
      }

      pauseViewportVideo(slide) {
        const mobileVideo = slide.querySelector('.product__mobile-inline-video');
        if (mobileVideo) {
          mobileVideo.pause();
          return;
        }

        const deferredMedia = slide.querySelector('deferred-media[loaded]');
        if (!deferredMedia) return;

        const video = deferredMedia.querySelector('video');
        if (video) {
          video.pause();
          return;
        }

        const iframe = deferredMedia.querySelector('iframe');
        if (!iframe) return;

        if (iframe.classList.contains('js-youtube')) {
          iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
          return;
        }

        if (iframe.classList.contains('js-vimeo')) {
          iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
